<template>
  <div class="tw-bg-white tw-rounded tw-border tw-border-gray-200">
    <div class="video-container tw-h-[550px] tw-w-full tw-relative tw-rounded tw-overflow-hidden tw-bg-gray-200">
      <!-- Video Header that disappears when video starts -->
      <transition name="fade-out">
        <div v-show="showVideoHeader && shouldShowIframe" class="video-header tw-absolute tw-inset-0 tw-z-20 tw-p-6 tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-700 tw-text-white tw-overflow-hidden">
          <!-- Background Pattern -->
          <div class="tw-absolute tw-inset-0 tw-opacity-10">
            <div class="tw-absolute tw-top-0 tw-right-0 tw-w-32 tw-h-32 tw-bg-white tw-rounded-full tw-transform tw-translate-x-16 -tw-translate-y-16"></div>
            <div class="tw-absolute tw-bottom-0 tw-left-0 tw-w-24 tw-h-24 tw-bg-white tw-rounded-full tw-transform -tw-translate-x-12 tw-translate-y-12"></div>
          </div>
          
          <!-- Header Content -->
          <div class="tw-relative tw-z-10">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
              <h1 class="tw-text-2xl tw-font-bold">{{ session?.name || 'Live Session' }}</h1>
              <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-[60px] tw-h-[30px] tw-bg-red-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-xs tw-animate-pulse">
                  LIVE
                </div>
                <div class="tw-text-sm tw-opacity-90">{{ countdownText }}</div>
              </div>
            </div>
            
            <div class="tw-flex tw-items-center tw-gap-4 tw-text-sm tw-opacity-90">
              <span class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:calendar-bold" class="tw-text-white" />
                {{ formattedSessionDate }}
              </span>
              <span class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:clock-circle-bold" class="tw-text-white" />
                {{ formattedDuration }}
              </span>
              <span class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:user-bold" class="tw-text-white" />
                {{ speakerNames }}
              </span>
              <span v-if="session?.attendees_count" class="tw-flex tw-items-center tw-gap-2">
                <icon icon="solar:users-group-rounded-bold" class="tw-text-white" />
                {{ session.attendees_count }} attending
              </span>
            </div>
            
            <p class="tw-text-sm tw-opacity-80 tw-mt-3">
              {{ session?.description || 'Join us for an exclusive live session.' }}
            </p>
            
            <!-- Centered Counter and Button -->
            <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mt-6 tw-gap-4">
              <!-- Large Counter Display -->
              <div class="tw-text-center">
                <div class="tw-text-6xl tw-font-bold tw-text-white tw-mb-2">{{ countdown }}</div>
                <div class="tw-text-lg tw-text-white tw-opacity-90">seconds until session starts</div>
              </div>
            </div>
          </div>
        </div>
      </transition>
      
      <iframe 
        v-if="shouldShowIframe"
        ref="videoIframe"
        class="tw-w-full tw-h-full"
        :src="session?.embed_link" 
        title="Video player" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
        referrerpolicy="strict-origin-when-cross-origin" 
        allowfullscreen>
      </iframe>
      
      <div v-if="shouldShowIframe" class="video-overlay">
        <div class="tw-w-[60px] tw-h-[30px] tw-bg-red-600 tw-top-5 tw-right-5 tw-absolute tw-rounded tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-xs">
          LIVE
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue'
import moment from 'moment'

const props = defineProps({
  session: {
    type: Object,
    required: true
  },
  shouldShowIframe: {
    type: Boolean,
    default: false
  },
  showVideoHeader: {
    type: Boolean,
    default: true
  },
  countdown: {
    type: Number,
    default: 5
  }
})

// Computed properties for data formatting
const formattedSessionDate = computed(() => {
  if (!props.session?.start_date) return 'Date not specified'
  
  try {
    const dateObj = moment(props.session.start_date)
    const today = moment()
    const tomorrow = moment().add(1, 'day')

    if (dateObj.isSame(today, 'day')) {
      return `Today ${dateObj.format('h:mm A')}`
    } else if (dateObj.isSame(tomorrow, 'day')) {
      return `Tomorrow ${dateObj.format('h:mm A')}`
    } else {
      return dateObj.format('MMM D, h:mm A')
    }
  } catch (error) {
    console.error('Error formatting session date:', error)
    return 'Date not specified'
  }
})

const formattedDuration = computed(() => {
  if (!props.session?.start_date || !props.session?.end_date) return 'Duration not specified'
  
  try {
    const start = moment(props.session.start_date)
    const end = moment(props.session.end_date)
    const duration = moment.duration(end.diff(start))
    const hours = Math.floor(duration.asHours())
    const minutes = duration.minutes()
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  } catch (error) {
    console.error('Error calculating duration:', error)
    return 'Duration not specified'
  }
})

const speakerNames = computed(() => {
  if (!props.session?.speakers || props.session.speakers.length === 0) {
    return 'No speakers assigned'
  }
  
  if (props.session.speakers.length === 1) {
    return props.session.speakers[0].name || 'Speaker'
  }
  
  const names = props.session.speakers
    .slice(0, 2)
    .map(s => s.name || 'Speaker')
    .join(', ')
  
  if (props.session.speakers.length > 2) {
    return `${names} and ${props.session.speakers.length - 2} more`
  }
  
  return names
})

const countdownText = computed(() => {
  if (props.countdown > 0) {
    return `Starting in ${props.countdown}s`
  }
  return 'Starting now...'
})
</script>

<style scoped>
.video-container {
  position: relative;
  display: block;
}

.video-container iframe {
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 10;
  pointer-events: none;
}

/* Additional CSS to ensure no interactions */
.video-container * {
  pointer-events: none !important;
}

/* Video Header Styles */
.video-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: auto;
}

.video-header button {
  pointer-events: auto;
}

.video-header * {
  pointer-events: auto;
}

/* Fade Out Transition */
.fade-out-enter-active,
.fade-out-leave-active {
  transition: all 0.8s ease-in-out;
}

.fade-out-enter-from {
  opacity: 1;
  transform: translateY(0);
}

.fade-out-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-out-enter-to,
.fade-out-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
